/**
 * 押すと右パネルで開く表の行の、押し方と選んだ印。
 *
 * **行そのものにボタンの役を持たせない。** `<tr>` に `role="button"` を付けると表の行としての
 * 意味が消え、支援技術では列見出しと値の対応が読めなくなる。代わりに行の先頭のセル（部品名）を
 * 本物の `<button>` にし、キーボード（Enter ・ Space）と支援技術（開いているか）はそのボタンが
 * 受け持つ。行のどこを押しても開けるように、クリックは行で受ける — ボタンのクリックも行へ
 * 伝わるので、ボタン自身は動きを持たない（持たせると 1 回の押下で 2 回切り替わる）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルで開く表の行を、行のクリックと先頭のボタン（Enter ・ Space）で開けるようにし、選んだ行の印を揃える
 * @graph-connects none
 */
import type { ReactNode } from 'react';

/** 押せる行の見た目（指したときの地色）。 @graph-connects none */
export const SELECTABLE_ROW_CLASS = 'cursor-pointer hover:bg-[var(--color-bg-hover)]';

/** 選んだ行の印（左端の線と地色）。 @graph-connects none */
export const SELECTED_ROW_CLASS =
  'bg-[var(--color-bg-active)] shadow-[inset_4px_0_0_var(--color-accent-primary)]';

/**
 * 行へ渡すクリックの受け口と見た目をまとめる。
 *
 * @param onSelect 行を押したときの動き
 * @param selected 選んでいる行か
 * @returns 行へ渡すクリック・印の属性と class
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 表の行をクリックで選べるようにし、選んでいれば印の class を足す。
 * @graph-connects SELECTABLE_ROW_CLASS [references] 押せる行の見た目
 * @graph-connects SELECTED_ROW_CLASS [references] 選んだ行の印
 */
export function selectableRowProps(onSelect: () => void, selected: boolean) {
  return {
    'data-selected': selected,
    className: selected ? `${SELECTABLE_ROW_CLASS} ${SELECTED_ROW_CLASS}` : SELECTABLE_ROW_CLASS,
    onClick: onSelect,
  };
}

/**
 * 行の先頭のセルに置く、行を開くボタン。見た目は地の文字のままにする（セルのリンクに見せない）。
 *
 * 押下は行の `onClick` へ伝わって行が開く。`aria-expanded` で右パネルが開いているかを伝える。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 押せる行の先頭に、キーボードと支援技術から行を開ける地の文字のボタンを描く。
 * @graph-connects none
 */
export function RowOpenButton({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      data-row-open
      className="cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-border-focus)]"
    >
      {children}
    </button>
  );
}
